/**
 * QAAutoFixPreviewModal — before/after diff modal for QA Auto-Fix.
 *
 * Renders the dry-run report from POST /api/admin/content-packages/{id}/qa-fix?dry_run=true
 * so the admin can review every text rewrite (especially fear/marketing-term
 * softening) before committing.
 *
 * Pure presentational — caller owns open state, loading state, and the
 * final "Apply" callback.
 */
import { useMemo } from "react";
import { X, Wrench, Loader2, Check, AlertCircle, Image as ImageIcon } from "lucide-react";

/**
 * Lightweight word-level diff. Returns an array of {token, kind} tuples
 * where kind ∈ {'same'|'removed'|'added'}. Designed for short fields like
 * titles and meta descriptions — for longer bodies we fall back to a
 * full block-level before/after view.
 */
function tokenDiff(before, after) {
    const a = (before || "").split(/(\s+)/);
    const b = (after || "").split(/(\s+)/);
    const setB = new Set(b.map((t) => t.toLowerCase()));
    const setA = new Set(a.map((t) => t.toLowerCase()));
    const removed = a.map((t) => ({
        token: t,
        kind: setB.has(t.toLowerCase()) ? "same" : "removed",
    }));
    const added = b.map((t) => ({
        token: t,
        kind: setA.has(t.toLowerCase()) ? "same" : "added",
    }));
    return { removed, added };
}

function InlineDiff({ before, after }) {
    const { removed, added } = useMemo(() => tokenDiff(before, after), [before, after]);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-bl-muted font-semibold">
                    Before
                </div>
                <div className="bl-card p-2 leading-snug whitespace-pre-wrap">
                    {removed.length === 0 ? (
                        <span className="text-bl-muted italic">(empty)</span>
                    ) : (
                        removed.map((t, i) => (
                            <span
                                key={i}
                                className={
                                    t.kind === "removed"
                                        ? "bg-[var(--bl-rust)]/15 text-[var(--bl-rust)] line-through rounded px-0.5"
                                        : ""
                                }
                            >
                                {t.token}
                            </span>
                        ))
                    )}
                </div>
            </div>
            <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-bl-muted font-semibold">
                    After
                </div>
                <div className="bl-card p-2 leading-snug whitespace-pre-wrap">
                    {added.length === 0 ? (
                        <span className="text-bl-muted italic">(empty)</span>
                    ) : (
                        added.map((t, i) => (
                            <span
                                key={i}
                                className={
                                    t.kind === "added"
                                        ? "bg-[var(--bl-green)]/15 text-[var(--bl-green)] rounded px-0.5"
                                        : ""
                                }
                            >
                                {t.token}
                            </span>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default function QAAutoFixPreviewModal({
    open,
    report,
    loading,
    applying,
    onClose,
    onApply,
}) {
    if (!open) return null;

    const diffs = report?.diffs || [];
    const appliedFixes = report?.applied_fixes || [];
    const qaAfter = report?.qa_status || {};
    const remainingErrors = qaAfter.errors ?? 0;
    const remainingWarnings = qaAfter.warnings ?? 0;
    const imagesQueued = report?.images_regen_queued || 0;
    const noChanges = !loading && appliedFixes.length === 0 && imagesQueued === 0;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            data-testid="qa-autofix-preview-modal"
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-bl-bg bl-card max-w-4xl w-full max-h-[88vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 p-5 border-b border-bl/30">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Wrench className="h-5 w-5 text-bl-primary" />
                            <h2 className="font-display font-semibold text-xl text-bl tracking-tight">
                                QA Auto-Fix · Preview
                            </h2>
                        </div>
                        <p className="text-xs text-bl-muted mt-1 max-w-2xl">
                            Review every rewrite before it&apos;s committed. Nothing is saved until you click <strong>Apply Fix</strong>.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={applying}
                        className="text-bl-muted hover:text-bl"
                        data-testid="qa-autofix-modal-close"
                        aria-label="Close preview"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-bl-muted">
                            <Loader2 className="h-4 w-4 animate-spin" /> Computing changes…
                        </div>
                    ) : noChanges ? (
                        <div
                            className="bl-card p-4 flex items-start gap-3"
                            data-testid="qa-autofix-modal-empty"
                        >
                            <Check className="h-5 w-5 text-[var(--bl-green)] mt-0.5" />
                            <div>
                                <div className="font-semibold text-bl">
                                    Nothing to auto-fix.
                                </div>
                                <p className="text-xs text-bl-muted mt-1">
                                    Every QA finding requires manual attention (e.g. empty article body, missing real-world content). Open the article tab and edit directly.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Summary bar */}
                            <div
                                className="bl-card p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs"
                                data-testid="qa-autofix-modal-summary"
                            >
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-bl-muted">
                                        Fixes
                                    </div>
                                    <div className="font-display font-semibold text-lg text-bl-primary">
                                        {appliedFixes.length}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-bl-muted">
                                        Diffs
                                    </div>
                                    <div className="font-display font-semibold text-lg text-bl">
                                        {diffs.length}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-bl-muted">
                                        QA after
                                    </div>
                                    <div className={`font-display font-semibold text-lg ${qaAfter.ok ? "text-[var(--bl-green)]" : "text-[var(--bl-rust)]"}`}>
                                        {qaAfter.score ?? "—"}/100
                                    </div>
                                    <div className="text-[10px] text-bl-muted">
                                        {remainingErrors} error{remainingErrors === 1 ? "" : "s"} · {remainingWarnings} warn
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-bl-muted flex items-center gap-1">
                                        <ImageIcon className="h-3 w-3" /> Images
                                    </div>
                                    <div className="font-display font-semibold text-lg text-bl">
                                        {imagesQueued}
                                    </div>
                                    <div className="text-[10px] text-bl-muted">
                                        will re-queue
                                    </div>
                                </div>
                            </div>

                            {/* Applied fix descriptions */}
                            {appliedFixes.length > 0 && (
                                <section data-testid="qa-autofix-modal-fixes">
                                    <h3 className="text-xs font-semibold uppercase tracking-wider text-bl-muted mb-2">
                                        Fixes that will be applied
                                    </h3>
                                    <ul className="space-y-1 text-sm text-bl">
                                        {appliedFixes.map((line, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <Check className="h-3.5 w-3.5 text-[var(--bl-green)] mt-1 shrink-0" />
                                                <span>{line}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {/* Before/after diffs */}
                            {diffs.length > 0 && (
                                <section data-testid="qa-autofix-modal-diffs">
                                    <h3 className="text-xs font-semibold uppercase tracking-wider text-bl-muted mb-2">
                                        Before → After
                                    </h3>
                                    <div className="space-y-4">
                                        {diffs.map((d, i) => (
                                            <div
                                                key={i}
                                                className="space-y-2"
                                                data-testid={`qa-autofix-diff-${d.field}`}
                                            >
                                                <div className="text-xs font-semibold text-bl">
                                                    {d.label}
                                                </div>
                                                <InlineDiff before={d.before} after={d.after} />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Remaining manual findings */}
                            {remainingErrors + remainingWarnings > 0 && (
                                <section data-testid="qa-autofix-modal-remaining">
                                    <h3 className="text-xs font-semibold uppercase tracking-wider text-bl-muted mb-2 flex items-center gap-1.5">
                                        <AlertCircle className="h-3.5 w-3.5 text-[var(--bl-amber)]" />
                                        Still need manual attention
                                    </h3>
                                    <ul className="space-y-1 text-xs">
                                        {(report?.remaining_findings || [])
                                            .filter((f) => f.severity !== "info")
                                            .map((f, i) => (
                                                <li
                                                    key={i}
                                                    className={
                                                        f.severity === "error"
                                                            ? "text-[var(--bl-rust)]"
                                                            : "text-[var(--bl-amber)]"
                                                    }
                                                >
                                                    [{f.severity}] {f.code}: {f.message}
                                                </li>
                                            ))}
                                    </ul>
                                </section>
                            )}
                        </>
                    )}
                </div>

                {/* Footer actions */}
                <div className="border-t border-bl/30 p-4 flex items-center justify-between gap-3">
                    <span className="text-[11px] text-bl-muted">
                        Auto-fix is deterministic — no LLM call. Image regenerations queue separately.
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={applying}
                            className="bl-btn-ghost text-xs"
                            data-testid="qa-autofix-modal-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={onApply}
                            disabled={loading || applying || noChanges}
                            className="bl-btn-primary text-xs gap-1.5"
                            data-testid="qa-autofix-modal-apply"
                        >
                            {applying ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Check className="h-3.5 w-3.5" />
                            )}
                            {applying ? "Applying…" : "Apply Fix"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
